import { request } from './client';

/**
 * The Banterboard.
 *
 * Boards are addressed by what they belong to — the house, or a league tournament —
 * never by a board id. The server resolves the board and then checks the caller
 * against that scope, so there is no id to guess your way through.
 *
 * A tournament board answers 404 rather than 403 when you are not a member: a 403
 * would confirm the league exists, which is enough to go looking for others.
 */

/** The House board. Read-only for players — only the Gaffer posts here. */
export function getHouseBoard({ before, beforeId } = {}) {
  return request(`/api/customer/banter/house${cursor(before, beforeId)}`);
}

/** A league tournament's board. Members only. The platform is 18+ throughout. */
export function getLeagueBoard(tournamentId, { before, beforeId } = {}) {
  return request(
    `/api/customer/banter/league-tournament/${tournamentId}${cursor(before, beforeId)}`
  );
}

/**
 * The board to show on a game.
 *
 * For a game in a league this is the league's board — the same thread, reachable from
 * either place, so a group is not split across two quiet boards. For a standalone game
 * it is that game's own, which closes when the game ends.
 *
 * Unlike the other two, this answers an object rather than an array: `{ posts, open }`.
 * `open` is false once a standalone game is over, and the composer needs to know that
 * before it renders rather than by having a post rejected.
 */
export function getGameBoard(gameId, { before, beforeId } = {}) {
  return request(`/api/customer/banter/game/${gameId}${cursor(before, beforeId)}`);
}

/** Post to whichever board that game shows. Refused once a standalone game is over. */
export function postToGameBoard(gameId, { phraseId, targetPlayerId, targetTeamId }) {
  return request(`/api/customer/banter/game/${gameId}/posts`, {
    method: 'POST',
    body: { phraseId, targetPlayerId, targetTeamId },
  });
}

/**
 * Who and what may be named on a board, as `{ players, teams }`.
 *
 * Asked of the server rather than assembled here, because both halves are facts about the
 * board and the portal had no way to know either. The players are the board's own roster
 * as `{ playerId, playerName }` - a league's game answers with the league's members, since
 * that is whose thread it is - and the teams are the competition's rather than every team
 * on the platform.
 *
 * Behind the same membership gate as the board itself: a roster is not public.
 */
export function getLeagueBoardTargets(tournamentId) {
  return request(`/api/customer/banter/league-tournament/${tournamentId}/targets`);
}

export function getGameBoardTargets(gameId) {
  return request(`/api/customer/banter/game/${gameId}/targets`);
}

/**
 * Everything a player is allowed to say, by theme (item 63): switched-on themes in the admin's
 * order, each with its lines. An empty theme is not sent. The Gaffer's own lines are never in here.
 */
export function getThemes() {
  return request('/api/customer/banter/themes');
}

/**
 * Post by choosing, not by typing.
 *
 * Only the phrase id and its targets go up — the server renders the line. Sending
 * text would achieve nothing, which is rather the point: with no free-text path
 * there is nothing to moderate after the fact.
 */
export function postPhrase(tournamentId, { phraseId, targetPlayerId, targetTeamId }) {
  return request(`/api/customer/banter/league-tournament/${tournamentId}/posts`, {
    method: 'POST',
    body: { phraseId, targetPlayerId, targetTeamId },
  });
}

// Paging is keyed on the last post seen rather than an offset — a board grows while
// it is being read, and an offset would quietly repeat or skip messages.
function cursor(before, beforeId) {
  if (!before || !beforeId) return '';
  return `?before=${encodeURIComponent(before)}&beforeId=${encodeURIComponent(beforeId)}`;
}
